import axios from 'axios'
import type { AxiosResponse } from 'axios'

// API响应类型
export interface APIResponse<T> {
  code: number
  data: T
  message: string
}

// 用户类型
export interface User {
  id: number
  phone: string
  role: string
  status: string
  created_at?: string
  name?: string
  kyc_level?: string
  country_code?: string
  currency?: string
}

// 登录凭证
export interface LoginCredentials {
  phone: string
  password?: string
  code?: string
  remember_me?: boolean
}

// 注册数据
export interface RegisterData {
  phone: string
  password: string
  code: string
}

// 认证响应
export interface AuthResponse {
  access_token: string
  refresh_token: string
  expires_in: number
  user: User
}

// 发送验证码请求
export interface SendCodeRequest {
  phone: string
  type: 'register' | 'login' | 'reset_password'
}

// 验证验证码请求
export interface VerifyCodeRequest {
  phone: string
  code: string
  type: 'register' | 'login' | 'reset_password'
}

// 重置密码请求
export interface ResetPasswordRequest {
  phone: string
  code: string
  new_password: string
}

// 刷新Token请求
export interface RefreshTokenRequest {
  refresh_token: string
}

// 登出请求
export interface LogoutRequest {
  refresh_token: string
}

// 创建axios实例
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Token刷新状态追踪
let isRefreshing = false
let refreshPromise: Promise<boolean> | null = null

// 刷新Token函数
const refreshAccessToken = async (): Promise<boolean> => {
  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) {
    return false
  }

  try {
    // 使用原生axios避免拦截器循环调用
    const response = await axios.post(
      `${import.meta.env.VITE_API_BASE_URL || '/api/v1'}/auth/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    )

    const data = response.data
    if (data.code === 0 && data.data?.access_token) {
      localStorage.setItem('access_token', data.data.access_token)
      localStorage.setItem('refresh_token', data.data.refresh_token)
      return true
    }
    return false
  } catch (err) {
    console.error('Token refresh failed:', err)
    return false
  }
}

// 请求拦截器 - 添加token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// 响应拦截器 - 统一处理
apiClient.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config

    // 如果是401错误且不是刷新token的请求，尝试刷新token
    if (error.response?.status === 401 && !originalRequest._retry) {
      // 防止重复刷新
      if (isRefreshing) {
        if (refreshPromise) {
          const success = await refreshPromise
          if (success) {
            originalRequest.headers.Authorization = `Bearer ${localStorage.getItem('access_token')}`
            return apiClient(originalRequest)
          }
        }
        // 刷新失败，跳转登录
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        window.location.href = '/login'
        return Promise.reject(error)
      }

      isRefreshing = true
      originalRequest._retry = true

      refreshPromise = refreshAccessToken()
      const success = await refreshPromise
      isRefreshing = false
      refreshPromise = null

      if (success) {
        // 刷新成功，重试原请求
        originalRequest.headers.Authorization = `Bearer ${localStorage.getItem('access_token')}`
        return apiClient(originalRequest)
      }
    }

    // Only clear tokens and redirect on 401 (authentication failure)
    // Other errors (403, 404, 500) should not log the user out
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// 认证API
export const authApi = {
  // 发送验证码
  sendCode: (data: SendCodeRequest): Promise<APIResponse<void>> =>
    apiClient.post('auth/send-code', data),

  // 验证验证码
  verifyCode: (data: VerifyCodeRequest): Promise<APIResponse<void>> =>
    apiClient.post('auth/verify-code', data),

  // 用户注册
  register: (data: RegisterData): Promise<APIResponse<AuthResponse>> =>
    apiClient.post('auth/register', data),

  // 用户登录
  login: (data: LoginCredentials): Promise<APIResponse<AuthResponse>> =>
    apiClient.post('auth/login', data),

  // 刷新Token
  refreshToken: (data: RefreshTokenRequest): Promise<APIResponse<AuthResponse>> =>
    apiClient.post('auth/refresh', data),

  // 用户登出
  logout: (data: LogoutRequest): Promise<APIResponse<void>> =>
    apiClient.post('auth/logout', data),

  // 重置密码
  resetPassword: (data: ResetPasswordRequest): Promise<APIResponse<void>> =>
    apiClient.post('auth/reset-password', data),

  // 获取当前用户
  getCurrentUser: (): Promise<APIResponse<{ user: User }>> =>
    apiClient.get('users/me'),
}

export default authApi
